import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";

const DEFAULT_ZOOM = 12;
const MAP_CONTAINER_STYLE = { width: "100%", height: "280px", borderRadius: "8px" };

export interface BranchLocationMapProps {
	latitude?: number | null;
	longitude?: number | null;
	defaultCenter: { lat: number; lng: number };
	onChange?: (lat: number, lng: number) => void;
	readOnly?: boolean;
	className?: string;
}

const BranchLocationMap: React.FC<BranchLocationMapProps> = ({
	latitude,
	longitude,
	onChange,
	defaultCenter,
	readOnly = false,
	className = "",
}) => {
	const { t } = useTranslation();
	const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

	const { isLoaded, loadError } = useJsApiLoader({
		id: "branch-google-map-script",
		googleMapsApiKey: apiKey,
		preventGoogleFontsLoading: true,
	});

	const position = useMemo(() => {
		if (latitude != null && longitude != null) {
			return { lat: Number(latitude), lng: Number(longitude) };
		}
		return null;
	}, [latitude, longitude]);

	const onMapClick = useCallback(
		(e: google.maps.MapMouseEvent) => {
			if (readOnly || !onChange || !e.latLng) return;
			const lat = e.latLng.lat();
			const lng = e.latLng.lng();
			onChange(lat, lng);
		},
		[readOnly, onChange],
	);

	if (!apiKey) {
		return (
			<div
				className={`flex items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-6 text-sm text-muted-foreground ${className}`}
			>
				{t("branches.fields.locationApiKeyRequired")}
			</div>
		);
	}

	if (loadError) {
		return (
			<div
				className={`flex items-center justify-center rounded-lg border border-dashed border-destructive/50 bg-destructive/5 p-6 text-sm text-destructive ${className}`}
			>
				{t("branches.fields.locationLoadError")}
			</div>
		);
	}

	if (!isLoaded) {
		return (
			<div
				className={`flex items-center justify-center rounded-lg border border-muted bg-muted/30 p-6 text-sm text-muted-foreground ${className}`}
			>
				{t("common.loading")}
			</div>
		);
	}

	return (
		<div className={className}>
			{!readOnly && <p className="my-1 text-xs text-muted-foreground">{t("branches.fields.pickOnMap")}</p>}
			<GoogleMap
				mapContainerStyle={MAP_CONTAINER_STYLE}
				center={{
					lat: Number(defaultCenter.lat),
					lng: Number(defaultCenter.lng),
				}}
				zoom={position ? 15 : DEFAULT_ZOOM}
				onClick={onMapClick}
				options={{
					streetViewControl: false,
					mapTypeControl: true,
					fullscreenControl: true,
					zoomControl: true,
					clickableIcons: false,
					gestureHandling: "cooperative",
				}}
			>
				{position && <Marker position={position} draggable onDragEnd={onMapClick} />}
			</GoogleMap>
		</div>
	);
};

export default BranchLocationMap;
